/**
 * Global Data Lake marks. Drawn in SVG with currentColor and the
 * --brand-nav-indicator variable, so they take the colours of whichever Howden
 * theme is active.
 *
 * These are product marks for the portal, not the Howden logo. Check the
 * chosen one with the Digital Customer Experience team before it is used
 * outside the portal.
 */
const ACCENT = 'var(--brand-nav-indicator, #7ce0d3)';

type MarkProps = { size?: number };

const svgProps = (size: number) => ({
  width: size,
  height: size,
  viewBox: '0 0 32 32',
  'aria-hidden': true,
  focusable: false,
});

const SPOKES = [0, 60, 120, 180, 240, 300].map(deg => (deg * Math.PI) / 180);

/** A. Hub and spoke: the GDL architecture, one hub and its regional spokes. */
export const HubSpokeMark = ({ size = 32 }: MarkProps) => (
  <svg {...svgProps(size)}>
    <g stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      {SPOKES.map(r => (
        <line
          key={r}
          x1={16 + Math.cos(r) * 5.5}
          y1={16 + Math.sin(r) * 5.5}
          x2={16 + Math.cos(r) * 11}
          y2={16 + Math.sin(r) * 11}
        />
      ))}
    </g>
    {SPOKES.map(r => (
      <circle
        key={r}
        cx={16 + Math.cos(r) * 12.5}
        cy={16 + Math.sin(r) * 12.5}
        r="2.6"
        fill="currentColor"
      />
    ))}
    <circle cx="16" cy="16" r="5" fill={ACCENT} />
  </svg>
);

/** B. Hexagon lake: a platform hexagon holding the lake. */
export const HexLakeMark = ({ size = 32 }: MarkProps) => (
  <svg {...svgProps(size)}>
    <path
      d="M16 2.5 28 9.25v13.5L16 29.5 4 22.75V9.25Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path
      d="M9 14.5c2.3-1.7 4.7-1.7 7 0s4.7 1.7 7 0"
      fill="none"
      stroke={ACCENT}
      strokeWidth="2.4"
      strokeLinecap="round"
    />
    <path
      d="M9 19.5c2.3-1.7 4.7-1.7 7 0s4.7 1.7 7 0"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
    />
  </svg>
);

/** C. Globe: global coverage, with lake waves as latitude. */
export const GlobeMark = ({ size = 32 }: MarkProps) => (
  <svg {...svgProps(size)}>
    <circle
      cx="16"
      cy="16"
      r="13"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    />
    <ellipse
      cx="16"
      cy="16"
      rx="5.5"
      ry="13"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <path
      d="M4.5 12.5c3.8-2 7.7-2 11.5 0s7.7 2 11.5 0"
      fill="none"
      stroke={ACCENT}
      strokeWidth="2.2"
      strokeLinecap="round"
    />
    <path
      d="M4.5 19.5c3.8-2 7.7-2 11.5 0s7.7 2 11.5 0"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

/** D. Monogram: the GDL letters in an accent tile. */
export const MonogramMark = ({ size = 32 }: MarkProps) => (
  <svg {...svgProps(size)}>
    <rect x="1" y="1" width="30" height="30" rx="7" fill={ACCENT} />
    <text
      x="16"
      y="20.5"
      textAnchor="middle"
      fontFamily="Arial, sans-serif"
      fontWeight="700"
      fontSize="11.5"
      fill="var(--brand-nav-bg, #0857c3)"
    >
      GDL
    </text>
  </svg>
);

/** The mark the portal uses. Swap the component here to change it everywhere. */
export const GdlMark = HubSpokeMark;
